import { createContext, useCallback, useContext, useState } from 'react';

// Drop-in replacement for window.confirm(): renders a styled modal that
// matches the app's surface aesthetic instead of the ugly browser-native
// dialog. Returns a promise that resolves to true on Confirm, false on
// Cancel or backdrop-tap.
//
// Usage at the top of App: wrap children in <ConfirmProvider>. In any
// component below: `const confirm = useConfirm()` then
// `if (!(await confirm({ title: '…', body: '…', destructive: true }))) return;`

const ConfirmCtx = createContext(null);

export function ConfirmProvider({ children }) {
  const [open, setOpen] = useState(null); // null or { title, body, confirmLabel, cancelLabel, destructive, resolve }

  const confirm = useCallback((opts = {}) => {
    return new Promise((resolve) => {
      setOpen({
        title: opts.title || 'Are you sure?',
        body: opts.body || null,
        confirmLabel: opts.confirmLabel || 'Confirm',
        cancelLabel: opts.cancelLabel || 'Cancel',
        destructive: !!opts.destructive,
        resolve,
      });
    });
  }, []);

  const respond = (value) => {
    if (open) {
      open.resolve(value);
      setOpen(null);
    }
  };

  return (
    <ConfirmCtx.Provider value={confirm}>
      {children}
      {open && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="confirm-title"
          onClick={() => respond(false)}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 100,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(0,0,0,0.55)',
            backdropFilter: 'blur(4px)',
            padding: '24px',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="slide-in"
            style={{
              width: '100%',
              maxWidth: 420,
              background: 'var(--sheet-bg)',
              border: '1px solid var(--surface-border)',
              borderRadius: 18,
              padding: '24px 22px 22px',
              boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
            }}
          >
            <h3
              id="confirm-title"
              className="font-heading"
              style={{
                fontSize: 19,
                fontWeight: 600,
                margin: '0 0 8px',
                color: 'var(--text)',
                lineHeight: 1.35,
              }}
            >
              {open.title}
            </h3>
            {open.body && (
              <p
                style={{
                  fontSize: 15,
                  lineHeight: 1.55,
                  color: 'var(--text-muted)',
                  margin: '0 0 18px',
                }}
              >
                {open.body}
              </p>
            )}
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 8,
                marginTop: open.body ? 0 : 18,
              }}
            >
              <button
                type="button"
                onClick={() => respond(false)}
                style={{
                  padding: '10px 18px',
                  minHeight: 44,
                  borderRadius: 999,
                  background: 'transparent',
                  border: '1px solid var(--surface-border)',
                  color: 'var(--text-muted)',
                  fontSize: 16,
                  fontWeight: 600,
                  letterSpacing: '0.05em',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                {open.cancelLabel}
              </button>
              <button
                type="button"
                onClick={() => respond(true)}
                autoFocus
                // --on-alert is the text colour that reads on --alert in
                // each theme: white on the dark theme's salmon was 2.5:1.
                style={{
                  padding: '10px 18px',
                  minHeight: 44,
                  borderRadius: 999,
                  background: open.destructive ? 'var(--alert)' : 'var(--cyan)',
                  color: open.destructive ? 'var(--on-alert, #2a0808)' : 'var(--cyan-text)',
                  border: 'none',
                  fontSize: 16,
                  fontWeight: 600,
                  letterSpacing: '0.05em',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                {open.confirmLabel}
              </button>
            </div>
          </div>
        </div>
      )}
    </ConfirmCtx.Provider>
  );
}

export function useConfirm() {
  const fn = useContext(ConfirmCtx);
  if (!fn) {
    // Fall back to native confirm if the provider isn't mounted — keeps
    // unit tests and storybook-y contexts working without a wrapper.
    return (opts) => Promise.resolve(window.confirm(opts?.title || 'Are you sure?'));
  }
  return fn;
}
